import type { CategoryConfig, ChatMessageInputBarHandle } from '@if/mui'
import { TemplateSelector } from '@if/mui'
import type { ChatTemplateMessage } from '@if/core/types'
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn'
import { Chip, Divider, Stack, ToggleButton } from '@mui/material'
import { memo, useCallback, useState } from 'react'
import type { RefObject } from 'react'
import { useTranslation } from 'react-i18next'

interface QuickActionsBarProps {
  onFormatClick?: () => void
  formatMode?: boolean
  messageInputRef: RefObject<ChatMessageInputBarHandle | null>
  templates?: ChatTemplateMessage[]
  isLoadingTemplates?: boolean
  categories: Record<string, CategoryConfig>
  fetchedTemplate?: ChatTemplateMessage | null
  isFetchingTemplate?: boolean
  fetchError?: boolean
  onFetchTemplate?: (code: string) => void
}

const QuickActionsBar = ({
  onFormatClick,
  formatMode,
  messageInputRef,
  templates,
  isLoadingTemplates,
  categories,
  fetchedTemplate,
  isFetchingTemplate,
  fetchError,
  onFetchTemplate,
}: QuickActionsBarProps) => {
  const { t: tGlobal } = useTranslation('global')
  const { t } = useTranslation()
  const [activeCategory, setActiveCategory] = useState<string | null>(null)

  const handleQuickActionClick = useCallback((category: string) => {
    setActiveCategory(category)
  }, [])

  const handleTemplateSelect = useCallback(
    (template: ChatTemplateMessage) => {
      messageInputRef.current?.insertTemplate(template)
      setActiveCategory(null)
    },
    [messageInputRef],
  )

  const handleDialogClose = useCallback(() => {
    setActiveCategory(null)
  }, [])

  return (
    <>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap alignItems="center">
        <ToggleButton
          value="multiline"
          selected={formatMode ?? false}
          onChange={onFormatClick}
          size="small"
          color="info"
          sx={{ height: 24, py: 0, px: 1.5, borderRadius: '16px', textTransform: 'none', gap: 0.5 }}
        >
          <KeyboardReturnIcon sx={{ fontSize: '1rem' }} />
          {tGlobal('chat.multiline')}
        </ToggleButton>

        <Divider orientation="vertical" flexItem />

        {Object.entries(categories).map(([category, { Icon, color }]) => (
          <Chip
            key={category}
            icon={<Icon />}
            label={t(`instantMessage.category.${category}` as never)}
            size="small"
            variant="outlined"
            color={color ?? 'default'}
            clickable
            onClick={() => handleQuickActionClick(category)}
          />
        ))}
      </Stack>

      {activeCategory && (
        <TemplateSelector
          open
          onClose={handleDialogClose}
          onSelect={handleTemplateSelect}
          templates={templates ?? []}
          isLoadingTemplates={isLoadingTemplates ?? false}
          fetchedTemplate={fetchedTemplate}
          isFetchingTemplate={isFetchingTemplate}
          fetchError={fetchError}
          onFetchTemplate={onFetchTemplate}
          category={activeCategory}
          dialogTitle={`${tGlobal('chat.templateSelectTitle')} — ${String(t(`instantMessage.category.${activeCategory}` as never))}`}
        />
      )}
    </>
  )
}

export default memo(QuickActionsBar)
